import { useRef, useEffect, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { cn } from '../../utils';

interface InpaintingCanvasProps {
  readonly imageUrl: string;
  readonly brushSize: number;
  readonly isEraserMode: boolean;
  readonly brushColor?: 'red' | 'blue' | 'green';
  readonly onMaskUpdate: (maskData: string) => void;
  readonly className?: string;
  readonly disabled?: boolean;
}

export interface InpaintingCanvasRef {
  clearMask: () => void;
  downloadMask: () => void;
  getMaskData: () => string | null;
  undo: () => void;
}

export const InpaintingCanvas = forwardRef<InpaintingCanvasRef, InpaintingCanvasProps>(
  ({ imageUrl, brushSize, isEraserMode, brushColor = 'red', onMaskUpdate, className, disabled = false }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const maskCanvasRef = useRef<HTMLCanvasElement>(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);

    // Last drawn point — used to connect consecutive pointer events into smooth lines
    const lastPointRef = useRef<{ display: { x: number; y: number }; mask: { x: number; y: number } } | null>(null);

    // Circular buffer for undo states — stores both canvases together so undo
    // correctly reverts the sent mask as well as the visual overlay.
    const undoBufferRef = useRef<{ display: ImageData; mask: ImageData }[]>([]);
    const undoIndexRef = useRef<number>(-1);
    const maxUndoStates = 20;

    // Helper function to get canvas context with optimized settings for frequent reads
    const getOptimizedContext = (canvas: HTMLCanvasElement | null): CanvasRenderingContext2D | null => {
      if (!canvas) return null;
      return canvas.getContext('2d', { willReadFrequently: true });
    };

    // Get brush color RGB values
    const getBrushColorRGB = () => {
      switch (brushColor) {
        case 'red': return { r: 220, g: 38, b: 38 }; // Red
        case 'blue': return { r: 70, g: 130, b: 220 }; // Blue  
        case 'green': return { r: 34, g: 197, b: 94 }; // Green
        default: return { r: 220, g: 38, b: 38 }; // Default to red
      }
    };

    // Save current canvas state to undo buffer (circular buffer for efficiency)
    const saveCanvasState = useCallback(() => {
      const canvas = canvasRef.current;
      const maskCanvas = maskCanvasRef.current;
      if (!canvas || !maskCanvas) return;
      
      const ctx = getOptimizedContext(canvas);
      const maskCtx = getOptimizedContext(maskCanvas);
      if (!ctx || !maskCtx) return;
      
      try {
        const displayData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const maskData = maskCtx.getImageData(0, 0, maskCanvas.width, maskCanvas.height);

        // Use circular buffer instead of array operations for better performance
        undoIndexRef.current = (undoIndexRef.current + 1) % maxUndoStates;
        undoBufferRef.current[undoIndexRef.current] = { display: displayData, mask: maskData };
        
        // If buffer is not full yet, adjust the effective length
        if (undoBufferRef.current.length < maxUndoStates) {
          undoBufferRef.current.length = undoIndexRef.current + 1;
        }
      } catch (e) {
        console.warn('⚠️ Could not save canvas state for undo:', e);
      }
    }, []);

    // Undo last action
    const handleUndo = useCallback(() => {
      const canvas = canvasRef.current;
      const maskCanvas = maskCanvasRef.current;
      if (!canvas || !maskCanvas || undoBufferRef.current.length === 0) return;

      const ctx = getOptimizedContext(canvas);
      const maskCtx = getOptimizedContext(maskCanvas);
      if (!ctx || !maskCtx) return;

      // Get the saved state from circular buffer
      const previousState = undoBufferRef.current[undoIndexRef.current];
      
      if (previousState) {
        // Restore both the display canvas and the mask canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.putImageData(previousState.display, 0, 0);

        maskCtx.clearRect(0, 0, maskCanvas.width, maskCanvas.height);
        maskCtx.putImageData(previousState.mask, 0, 0);
        
        // Move index back in circular buffer
        if (undoIndexRef.current > 0) {
          undoIndexRef.current--;
        } else if (undoBufferRef.current.length === maxUndoStates) {
          undoIndexRef.current = maxUndoStates - 1;
        } else {
          // No more undo states available
          return;
        }
      }

      // Emit the updated mask data
      const maskData = maskCanvas.toDataURL('image/png');
      onMaskUpdate(maskData);
    }, [onMaskUpdate]);

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      clearMask: handleClearMask,
      downloadMask: handleDownloadMask,
      getMaskData: () => maskCanvasRef.current?.toDataURL('image/png') || null,
      undo: handleUndo,
    }));

    // Initialize canvases when image loads
    useEffect(() => {
      const canvas = canvasRef.current;
      const maskCanvas = maskCanvasRef.current;
      if (!canvas || !maskCanvas || !imageUrl) return;

      const img = new Image();
      img.crossOrigin = 'anonymous'; // Handle CORS if needed
      
      // Helper function to save canvas content before resize
      const saveCanvasContent = (canvas: HTMLCanvasElement): ImageData | null => {
        const ctx = getOptimizedContext(canvas);
        if (!ctx || canvas.width <= 0 || canvas.height <= 0) return null;
        
        try {
          return ctx.getImageData(0, 0, canvas.width, canvas.height);
        } catch (e) {
          console.warn('⚠️ Could not preserve canvas content during resize:', e);
          return null;
        }
      };

      // Helper function to restore canvas content after resize
      const restoreCanvasContent = (canvas: HTMLCanvasElement, imageData: ImageData): void => {
        const ctx = getOptimizedContext(canvas);
        if (!ctx) return;

        try {
          const tempCanvas = document.createElement('canvas');
          const tempCtx = tempCanvas.getContext('2d', { willReadFrequently: true });
          
          if (!tempCtx) return;
          
          tempCanvas.width = imageData.width;
          tempCanvas.height = imageData.height;
          tempCtx.putImageData(imageData, 0, 0);
          
          // Draw the scaled content to the resized canvas
          ctx.drawImage(tempCanvas, 0, 0, imageData.width, imageData.height, 0, 0, canvas.width, canvas.height);
        } catch (e) {
          console.warn('⚠️ Could not restore canvas content after resize:', e);
        }
      };

      // Helper function to resize canvas while preserving content
      const resizeCanvasWithContent = (canvas: HTMLCanvasElement, newWidth: number, newHeight: number): void => {
        const savedContent = saveCanvasContent(canvas);
        
        canvas.width = newWidth;
        canvas.height = newHeight;
        
        if (savedContent) {
          restoreCanvasContent(canvas, savedContent);
        }
      };

      // Helper function to update canvas positioning
      const updateCanvasPositioning = (canvas: HTMLCanvasElement, imageRect: DOMRect, offsetX: number, offsetY: number): void => {
        canvas.style.width = `${imageRect.width}px`;
        canvas.style.height = `${imageRect.height}px`;
        canvas.style.left = `${offsetX}px`;
        canvas.style.top = `${offsetY}px`;
        canvas.style.position = 'absolute';
      };
      
      const updateCanvasPosition = () => {
        // Find the parent container (should be the image container)
        const container = canvas.parentElement;
        if (!container) return;
        
        // Find the image element - it should be a sibling of the canvas
        const imageElement = container.querySelector('img');
        if (!imageElement) return;

        // Get the actual displayed image dimensions and position
        const imageRect = imageElement.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        
        // Position canvas to exactly match the image
        const offsetX = imageRect.left - containerRect.left;
        const offsetY = imageRect.top - containerRect.top;
        
        // Check if canvas dimensions need to change (this would clear the content)
        const needsResize = canvas.width !== imageRect.width || canvas.height !== imageRect.height;
        
        if (needsResize) {
          resizeCanvasWithContent(canvas, imageRect.width, imageRect.height);
        }
        
        // Update canvas positioning (this doesn't clear content)
        updateCanvasPositioning(canvas, imageRect, offsetX, offsetY);
      };
      
      img.onload = () => {
        // Mask canvas uses natural image dimensions for high-quality mask
        const { naturalWidth, naturalHeight } = img;
        maskCanvas.width = naturalWidth;
        maskCanvas.height = naturalHeight;
        
        // Initialize mask as black (no inpainting)
        const maskCtx = getOptimizedContext(maskCanvas);
        if (maskCtx) {
          maskCtx.fillStyle = 'black';
          maskCtx.fillRect(0, 0, naturalWidth, naturalHeight);
        }
        
        setImageLoaded(true);
        
        // Initial canvas positioning
        setTimeout(() => {
          updateCanvasPosition();
        }, 100); // Small delay to ensure image is rendered
      };
      
      img.onerror = () => {
        console.error('❌ Failed to load image for inpainting:', imageUrl);
      };
      
      img.src = imageUrl;

      // Set up resize observer to keep canvas aligned
      const resizeObserver = new ResizeObserver(() => {
        if (imageLoaded) {
          updateCanvasPosition();
        }
      });

      const container = canvas.parentElement;
      if (container) {
        resizeObserver.observe(container);
      }

      return () => {
        resizeObserver.disconnect();
      };
    }, [imageUrl, imageLoaded]);

    // Clear mask function
    const handleClearMask = useCallback(() => {
      const canvas = canvasRef.current;
      const maskCanvas = maskCanvasRef.current;
      if (!canvas || !maskCanvas) return;

      // Clear display canvas
      const ctx = getOptimizedContext(canvas);
      ctx?.clearRect(0, 0, canvas.width, canvas.height);

      // Reset mask to black
      const maskCtx = getOptimizedContext(maskCanvas);
      if (maskCtx) {
        maskCtx.fillStyle = 'black';
        maskCtx.fillRect(0, 0, maskCanvas.width, maskCanvas.height);
      }

      onMaskUpdate('');
    }, [onMaskUpdate]);

    // Download mask for debugging
    const handleDownloadMask = useCallback(() => {
      const maskCanvas = maskCanvasRef.current;
      if (!maskCanvas) return;

      const link = document.createElement('a');
      link.download = `inpainting-mask-${Date.now()}.png`;
      link.href = maskCanvas.toDataURL('image/png');
      link.click();
    }, []);

    // Get coordinates relative to canvas
    const getCanvasCoordinates = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      const maskCanvas = maskCanvasRef.current;
      if (!canvas || !maskCanvas) return null;

      const rect = canvas.getBoundingClientRect();
      
      // Get coordinates relative to display canvas
      const displayX = e.clientX - rect.left;
      const displayY = e.clientY - rect.top;
      
      // Scale to mask canvas coordinates (natural image size)
      const scaleX = maskCanvas.width / canvas.width;
      const scaleY = maskCanvas.height / canvas.height;
      
      return {
        display: { x: displayX, y: displayY },
        mask: { x: displayX * scaleX, y: displayY * scaleY }
      };
    }, []);

    // Drawing functions
    const startDrawing = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
      if (disabled || !imageLoaded) return;
      
      e.preventDefault(); // Prevent image dragging
      e.stopPropagation(); // Stop event bubbling

      // Capture the pointer so strokes continue even if the pointer moves off the canvas edge
      e.currentTarget.setPointerCapture(e.pointerId);
      
      // Save canvas state before starting to draw (for undo)
      saveCanvasState();

      // Seed last point so the first segment draws from the touch-down position
      const coords = getCanvasCoordinates(e);
      lastPointRef.current = coords;

      setIsDrawing(true);
      draw(e);
    }, [disabled, imageLoaded, saveCanvasState]);

    // Draw a smooth stroke segment between two points on both canvases
    const drawSegment = useCallback((
      ctx: CanvasRenderingContext2D,
      maskCtx: CanvasRenderingContext2D,
      from: { display: { x: number; y: number }; mask: { x: number; y: number } },
      to: { display: { x: number; y: number }; mask: { x: number; y: number } },
      maskBrushSize: number,
      displayBrushSize: number,
      brushColorRGB: { r: number; g: number; b: number }
    ) => {
      const { r, g, b } = brushColorRGB;

      // --- Mask canvas (high-res, binary white/black) ---
      maskCtx.globalCompositeOperation = 'source-over';
      maskCtx.strokeStyle = isEraserMode ? 'black' : 'white';
      maskCtx.lineWidth = maskBrushSize;
      maskCtx.lineCap = 'round';
      maskCtx.lineJoin = 'round';
      maskCtx.beginPath();
      maskCtx.moveTo(from.mask.x, from.mask.y);
      // Quadratic curve through midpoint for smooth Pencil strokes
      const midMaskX = (from.mask.x + to.mask.x) / 2;
      const midMaskY = (from.mask.y + to.mask.y) / 2;
      maskCtx.quadraticCurveTo(from.mask.x, from.mask.y, midMaskX, midMaskY);
      maskCtx.stroke();

      // --- Display canvas (visual overlay) ---
      if (isEraserMode) {
        ctx.globalCompositeOperation = 'destination-out';
        ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, 1)`;
      } else {
        ctx.globalCompositeOperation = 'source-over';
        ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, 0.7)`;
      }
      ctx.lineWidth = displayBrushSize;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(from.display.x, from.display.y);
      const midDispX = (from.display.x + to.display.x) / 2;
      const midDispY = (from.display.y + to.display.y) / 2;
      ctx.quadraticCurveTo(from.display.x, from.display.y, midDispX, midDispY);
      ctx.stroke();

      ctx.globalCompositeOperation = 'source-over';
    }, [isEraserMode]);

    const draw = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
      if (!isDrawing || disabled || !imageLoaded) {
        return;
      }
      
      e.preventDefault();
      e.stopPropagation();

      const canvas = canvasRef.current;
      const maskCanvas = maskCanvasRef.current;
      if (!canvas || !maskCanvas) return;

      const ctx = getOptimizedContext(canvas);
      const maskCtx = getOptimizedContext(maskCanvas);
      if (!ctx || !maskCtx) return;

      const maskBrushSize = brushSize * (maskCanvas.width / canvas.width);
      const brushColorRGB = getBrushColorRGB();

      // Use coalesced events when available — Apple Pencil fires high-frequency
      // intermediate positions between rendered frames that getCoalescedEvents exposes.
      const events: PointerEvent[] = (
        typeof e.nativeEvent.getCoalescedEvents === 'function'
          ? e.nativeEvent.getCoalescedEvents()
          : [e.nativeEvent]
      );

      for (const coalescedEvent of events) {
        // Build a synthetic coords object from the coalesced event
        const rect = canvas.getBoundingClientRect();
        const displayX = coalescedEvent.clientX - rect.left;
        const displayY = coalescedEvent.clientY - rect.top;
        const scaleX = maskCanvas.width / canvas.width;
        const scaleY = maskCanvas.height / canvas.height;
        const currentCoords = {
          display: { x: displayX, y: displayY },
          mask: { x: displayX * scaleX, y: displayY * scaleY },
        };

        const from = lastPointRef.current ?? currentCoords;
        drawSegment(ctx, maskCtx, from, currentCoords, maskBrushSize, brushSize, brushColorRGB);
        lastPointRef.current = currentCoords;
      }

      // Emit mask data
      const maskData = maskCanvas.toDataURL('image/png');
      onMaskUpdate(maskData);
    }, [isDrawing, disabled, imageLoaded, brushSize, getBrushColorRGB, drawSegment, onMaskUpdate]);

    const stopDrawing = useCallback((e?: React.PointerEvent<HTMLCanvasElement>) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
        if (e.currentTarget.hasPointerCapture(e.pointerId)) {
          e.currentTarget.releasePointerCapture(e.pointerId);
        }
      }
      lastPointRef.current = null; // Reset so next stroke starts fresh
      setIsDrawing(false);
    }, []);

    // Custom cursor based on brush size and mode
    const getCursorStyle = () => {
      if (disabled || !imageLoaded) {
        return 'not-allowed';
      }
      
      // Show crosshair cursor when inpainting is active
      return 'crosshair';
    };

    return (
      <>
        {/* Display canvas for visual overlay - positioned absolutely to cover the image */}
        <canvas
          ref={canvasRef}
          className={cn(
            'absolute top-0 left-0 z-10',
            imageLoaded ? 'block' : 'hidden',
            className
          )}
          onPointerDown={startDrawing}
          onPointerMove={(e) => {
            if (isDrawing) {
              draw(e);
            }
          }}
          onPointerUp={stopDrawing}
          onPointerCancel={stopDrawing}
          onContextMenu={(e) => {
            e.preventDefault(); // Prevent default right-click context menu
          }}
          onDragStart={(e) => e.preventDefault()} // Prevent dragging
          style={{ 
            cursor: getCursorStyle(),
            userSelect: 'none', // Prevent text selection
            touchAction: 'none', // Prevent touch gestures
            pointerEvents: disabled ? 'none' : 'auto', // Enable/disable pointer events based on state
            opacity: disabled ? 0.5 : 1 // Use inline style for opacity control
          }}
        />
        
        {/* Hidden mask canvas */}
        <canvas
          ref={maskCanvasRef}
          className="hidden"
        />
        
        {/* Loading indicator */}
        {!imageLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded z-20">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Initializing canvas...
            </div>
          </div>
        )}
      </>
    );
  }
);

InpaintingCanvas.displayName = 'InpaintingCanvas';
